import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ROUTES } from '../config/constants'

const faqs = [
  {
    q: 'What video format or quality do I need to send?',
    a: "Any format works — MP4, MOV, MKV, AVI. Just share a Google Drive or OneDrive link. We handle videos from phone cameras, GoPros, and DSLRs. Even shaky phone footage works — our editors know how to work with it.",
    icon: '📹',
  },
  {
    q: 'Is the editing done by AI or by a real person?',
    a: 'Both. Our desktop tool uses AI to detect key moments (sixes, wickets, catches, run-outs). Then our cricket-expert editors do the final cinematic polish — music sync, slow-mo, captions. You get the speed of AI with the quality of a human editor.',
    icon: '🧠',
  },
  {
    q: "What if I'm not happy with the final reel?",
    a: "We'll re-edit it — free of charge, no questions asked. Your satisfaction is the only metric that matters to us. We've done this for 1,000+ reels and our re-edit rate is under 5%.",
    icon: '🔒',
  },
  {
    q: 'Can I get reels for multiple players from one match?',
    a: "Yes — that's literally our specialty. One match upload → separate highlight reels for every player you want. Batsmen get their shots, bowlers get their wickets, fielders get their catches. One upload, infinite reels.",
    icon: '👥',
  },
  {
    q: 'How long does delivery actually take?',
    a: 'Standard orders: 24–72 hours from the time we receive your footage. Rush orders (24-hour delivery) are available at checkout for an additional charge. We always notify you on WhatsApp/email when your reel is ready.',
    icon: '⚡',
  },
  {
    q: "What's the difference between the Upload Service and the Free Desktop Tool?",
    a: 'Upload Service = we do everything. You send the footage, we send back a cinematic reel. Desktop Tool = free software you install on your PC/Mac, where you mark timestamps yourself and export reels per player. Upload Service is for players who want zero effort. Desktop Tool is for coaches and teams who want full control.',
    icon: '🖥️',
  },
  {
    q: 'My match video is 2+ hours and very large. How do I send it?',
    a: 'Upload it to Google Drive or OneDrive and paste the shareable link in our upload form — no size limits. Alternatively, you can use WeTransfer for single large files. We never ask you to compress or trim the footage yourself.',
    icon: '☁️',
  },
  {
    q: 'Will my reel actually go viral?',
    a: "We can't guarantee virality (no one can), but we can guarantee IPL-level production quality that gives your reel the best possible chance. Our reels regularly hit 5K–50K organic views. The content that gets the most traction: clean sixes, diving catches, hat-trick wickets, and epic run-outs.",
    icon: '🔥',
  },
]

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(null)

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i)

  return (
    <section className="py-20 md:py-28 px-4 relative overflow-hidden">
      {/* Section divider */}
      <div className="section-divider max-w-4xl mx-auto mb-20" />

      <div className="container mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-xs font-bold text-pitch-green uppercase tracking-[0.2em] mb-4">
            Got Questions?
          </span>
          <h2 className="text-4xl md:text-5xl font-black mb-4">
            <span className="text-white">Everything You </span>
            <span className="gradient-text">Need to Know</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-lg mx-auto">
            The most common questions — answered honestly.
          </p>
        </motion.div>

        {/* FAQ List */}
        <div className="max-w-3xl mx-auto space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
                openIndex === i
                  ? 'border-pitch-green/40 bg-pitch-green/[0.04]'
                  : 'border-white/[0.08] bg-white/[0.02] hover:border-white/[0.16]'
              }`}
            >
              <button
                onClick={() => toggle(i)}
                className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 group"
                aria-expanded={openIndex === i}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl flex-shrink-0">{faq.icon}</span>
                  <span className={`font-semibold text-sm md:text-base transition-colors ${
                    openIndex === i ? 'text-pitch-green' : 'text-white group-hover:text-pitch-green'
                  }`}>
                    {faq.q}
                  </span>
                </div>
                <motion.div
                  animate={{ rotate: openIndex === i ? 45 : 0 }}
                  transition={{ duration: 0.2 }}
                  className={`w-6 h-6 rounded-full border flex items-center justify-center flex-shrink-0 transition-all ${
                    openIndex === i
                      ? 'border-pitch-green text-pitch-green bg-pitch-green/10'
                      : 'border-white/20 text-gray-400'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v14M5 12h14" />
                  </svg>
                </motion.div>
              </button>

              <AnimatePresence initial={false}>
                {openIndex === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <p className="px-6 pb-6 text-gray-400 text-sm leading-relaxed pl-[4.25rem]">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-gray-500 text-sm mb-4">Still have questions?</p>
          <a
            href="mailto:reelcricket@gmail.com"
            className="inline-flex items-center gap-2 text-pitch-green hover:text-neon-green font-semibold text-sm transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Email us and we'll reply within a few hours →
          </a>
        </motion.div>
      </div>
    </section>
  )
}
